import { post, get, httpDelete } from 'api/client';
import config from 'config';

export interface CognitoAccount {
  account_id: number;
  account_name: string;
  user_role: string;
  user_name: string;
  user_email: string;
}

export interface ValidateTokenResponse {
  status: boolean;
  cognito_sub: string;
  email: string;
  accounts: CognitoAccount[];
  needs_linking: boolean;
  message?: string;
}

export interface LinkAccountsResponse {
  status: boolean;
  message: string;
  linked_accounts: number[];
}

export interface FederatedLoginResponse {
  status: boolean;
  cognito_sub: string;
  email: string;
  accounts: CognitoAccount[];
  needs_account_selection: boolean;
}

export interface ResolveAccountsResponse {
  status: boolean;
  requires_selection: boolean;
  cognito_sub: string;
  email: string;
  needs_linking?: boolean;
  account?: CognitoAccount;
  accounts?: CognitoAccount[];
}

export interface SelectAccountResponse {
  status: boolean;
  account: CognitoAccount;
}

export interface CognitoIdentity {
  provider: string;
  provider_user_id: string;
  is_primary: boolean;
  is_linked_in_cognito: boolean;
  linked_at: string | null;
}

export interface GetIdentitiesResponse {
  status: boolean;
  cognito_sub: string;
  identities: CognitoIdentity[];
}

export interface RegisterParams {
  email: string;
  password: string;
  username: string;
  firstName: string;
  lastName: string;
  onboardingData?: Record<string, unknown>;
}

export interface RegisterFederatedParams {
  id_token: string;
  username: string;
  first_name: string;
  last_name: string;
  onboarding_data?: Record<string, unknown>;
}

export interface RegistrationResponse {
  status: boolean;
  account_id: number;
  user: {
    id: number;
    email: string;
    name: string;
    last_name: string;
    role: string;
  };
  message: string;
}

export interface CreateSessionTokenResponse {
  session_token: string;
}

export interface CreateSessionTokenResponse {
  session_token: string;
}

export interface ExchangeSessionTokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
  account: CognitoAccount & { onboarded?: boolean };
}

export interface RefreshTokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
}

const PUBLIC_BASE_URL = config.PUBLIC_API_SERVICE_ENDPOINT;

export const CognitoApi = {
  validateToken(params: {
    id_token?: string;
    access_token?: string;
  }): Promise<ValidateTokenResponse> {
    return post('/cognito/validate', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  linkAccounts(params: {
    id_token: string;
    account_ids?: number[];
  }): Promise<LinkAccountsResponse> {
    return post('/cognito/link', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  federatedLogin(params: {
    provider: 'Google' | 'Facebook' | 'Apple';
    provider_user_id: string;
    email: string;
    cognito_access_token: string;
    cognito_id_token: string;
  }): Promise<FederatedLoginResponse> {
    return post('/cognito/federated-login', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  resolveAccounts(params: {
    id_token?: string;
    access_token?: string;
  }): Promise<ResolveAccountsResponse> {
    return post('/cognito/resolve-accounts', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  selectAccount(params: {
    id_token: string;
    account_id: number;
  }): Promise<SelectAccountResponse> {
    return post('/cognito/select-account', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  getIdentities(): Promise<GetIdentitiesResponse> {
    return get('/cognito/identities');
  },

  linkIdentity(params: {
    provider: string;
    provider_user_id: string;
  }): Promise<{ status: boolean; message: string }> {
    return post('/cognito/link-identity', params);
  },

  unlinkIdentity(params: {
    provider: string;
    provider_user_id: string;
  }): Promise<{ status: boolean; message: string }> {
    return httpDelete('/cognito/unlink-identity', { data: params });
  },

  register(params: RegisterParams): Promise<RegistrationResponse> {
    return post('/register', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  registerFederated(
    params: RegisterFederatedParams,
  ): Promise<RegistrationResponse> {
    return post('/register/federated', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  createSessionToken(params: {
    id_token: string;
    access_token: string;
    refresh_token: string;
    account_id: number;
    origin: string;
  }): Promise<CreateSessionTokenResponse> {
    return post('/cognito/create-session-token', params, {
      baseURL: PUBLIC_BASE_URL,
    });
  },

  exchangeSessionToken(
    sessionToken: string,
  ): Promise<ExchangeSessionTokenResponse> {
    return post(
      '/cognito/exchange-session-token',
      { session_token: sessionToken },
      {
        baseURL: PUBLIC_BASE_URL,
      },
    );
  },

  refreshTokens(refreshToken: string): Promise<RefreshTokenResponse> {
    return post(
      '/cognito/refresh',
      { refresh_token: refreshToken },
      {
        baseURL: PUBLIC_BASE_URL,
      },
    );
  },
};

export default CognitoApi;
